/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, action, runInAction, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';

import { displayError } from '@amzn/base-ui/dist/helpers/notification';

import CopyToClipboard from '../../helpers/CopyToClipboard';

const openWindow = (url, windowFeatures) => {
  return window.open(url, '_blank', windowFeatures);
};

// expected props
// - environment (via prop)
// - scEnvironmentsStore (via injection)
class ScEnvironmentHttpConnections extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.processingId = '';
      this.appStreamGeneratingId = '';
      this.appStreamConnectingId = '';
      this.destinationUrl = undefined;
      this.streamingUrl = undefined;
    });
  }

  get isAppStreamEnabled() {
    return process.env.REACT_APP_IS_APP_STREAM_ENABLED === 'true';
  }

  get environment() {
    return this.props.scEnvironment;
  }

  get envsStore() {
    return this.props.scEnvironmentsStore;
  }

  getConnectionStore() {
    return this.envsStore.getScEnvConnectionStore(this.environment.id);
  }

  get connections() {
    const isHttp = scheme => scheme === 'http' || scheme === 'https' || _.isEmpty(scheme);
    const connections = this.environment.getConnections(item => isHttp(item.scheme));
    return connections;
  }

  handleConnect = id =>
    action(async () => {
      const store = this.getConnectionStore();
      const connections = this.environment.connections;
      const connectInfo = _.find(connections, ['id', id]) || {};
      let url = connectInfo.url;

      runInAction(() => {
        this.processingId = id;
      });

      try {
        if (url) {
          openWindow(url, 'noopener,noreferrer');
        } else {
          const urlObj = await store.createConnectionUrl(id);
          url = urlObj.url;

          if (url) {
            if (connectInfo.type.toLowerCase() === 'rstudiov2') {
              const rstudioUrl = url.split('?v=')[0];
              const token = url.split('auth-do-sign-in?')[1];
              const tokenKey = 'v';
              const tokenVal = token.split('v=')[1];
              const mapForm = document.createElement('form');
              mapForm.target = 'Map';
              mapForm.method = 'POST';
              mapForm.action = rstudioUrl;
              mapForm.style.display = 'none';

              const mapInput = document.createElement('input');
              mapInput.name = tokenKey;
              mapInput.value = decodeURIComponent(tokenVal);
              mapForm.appendChild(mapInput);

              document.body.appendChild(mapForm);
              mapForm.submit();
            } else {
              const newTab = openWindow('about:blank');
              newTab.location = url;
            }
          }
        }
      } catch (error) {
        displayError(error);
      } finally {
        runInAction(() => {
          this.processingId = '';
        });
      }
    });

  handleAppStreamConnect = (url, id) =>
    action(async () => {
      try {
        runInAction(() => {
          this.appStreamConnectingId = id;
        });
        if (url) {
          const newTab = openWindow('about:blank');
          newTab.location = url;
        }
      } catch (error) {
        displayError(error);
      } finally {
        runInAction(() => {
          this.appStreamConnectingId = '';
        });
      }
    });

  handleGenerateAppStreamUrl = id =>
    action(async () => {
      const store = this.getConnectionStore();
      runInAction(() => {
        this.appStreamGeneratingId = id;
      });
      try {
        const urlObj = await store.createConnectionUrl(id);
        runInAction(() => {
          this.destinationUrl = urlObj.appstreamDestinationUrl;
          this.streamingUrl = urlObj.url;
        });
      } catch (error) {
        displayError(error);
      } finally {
        runInAction(() => {
          this.appStreamGeneratingId = '';
        });
      }
    });

  render() {
    const env = this.environment;
    const state = env.state;
    const canConnect = state.canConnect;
    const connections = this.connections;
    if (!canConnect) return null;

    return (
      <div className="connection-card">
        <div className="connection-card-header">
          <div className="connection-card-title">
            <Icon name="globe" />
            Web Browser Connection
          </div>
        </div>
        <div className="connection-card-body">
          <div className="connection-intro">
            This opens your workspace application directly in a new browser tab. Simply click <b>Open in Browser</b> below.
            Please ensure popups are allowed for this site.
          </div>
          {this.isAppStreamEnabled && this.renderAppStreamInfo()}
          {this.renderBody(connections)}
          <div className="connection-help-link">
            <Icon name="question circle outline" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
            Need help? Visit the <a href="/documentation">Documentation</a> for detailed guides.
          </div>
        </div>
      </div>
    );
  }

  renderAppStreamInfo() {
    return (
      <div className="connection-info-box" data-testid="appstream-instructions-http">
        <Icon name="info circle" />
        <div className="connection-info-box-content">
          <b>AppStream Connection Instructions:</b>
          <ul>
            <li>Click "Generate URL" to create the destination URL</li>
            <li>Copy the URL and click "Connect"</li>
            <li>Paste the URL in the new AppStream Firefox tab</li>
          </ul>
          <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#6b7280' }}>
            Please allow popups for this domain to open AppStream in a new tab.
          </div>
        </div>
      </div>
    );
  }

  renderAppStreamBody(connections) {
    const appStreamGeneratingId = this.appStreamGeneratingId;
    const streamingUrl = this.streamingUrl;
    const destinationUrl = this.destinationUrl;
    const appStreamConnectingId = this.appStreamConnectingId;
    const isDisabled = (id1, id2) => id2 !== id1 && !_.isEmpty(id2);
    const isLoading = (id1, id2) => id2 === id1;

    return (
      <>
        {_.map(connections, item => (
          <div key={item.id}>
            <div className="connection-row">
              <div className="connection-row-name">{item.name || 'Web Application'}</div>
              <div className="connection-row-actions">
                <button
                  className="connection-btn connection-btn-primary"
                  disabled={isDisabled(item.id, appStreamGeneratingId)}
                  onClick={this.handleGenerateAppStreamUrl(item.id)}
                  data-testid="sc-environment-generate-url-button"
                >
                  {isLoading(item.id, appStreamGeneratingId) ? <Icon name="spinner" loading style={{ margin: 0 }} /> : <Icon name="linkify" style={{ margin: 0 }} />}
                  Generate URL
                </button>
              </div>
            </div>

            {destinationUrl && (
              <div className="connection-details fadeIn animated">
                <div className="credential-item">
                  <span className="credential-label">URL</span>
                  <span className="credential-value" data-testid="destination-url">{destinationUrl}</span>
                  <div className="credential-actions">
                    <CopyToClipboard text={destinationUrl} />
                  </div>
                </div>
                <div style={{ marginTop: '12px', textAlign: 'right' }}>
                  <button
                    className="connection-btn connection-btn-primary"
                    disabled={isDisabled(item.id, appStreamConnectingId)}
                    onClick={this.handleAppStreamConnect(streamingUrl, item.id)}
                    data-testid="connect-to-workspace-button"
                  >
                    {isLoading(item.id, appStreamConnectingId) ? <Icon name="spinner" loading style={{ margin: 0 }} /> : <Icon name="external" style={{ margin: 0 }} />}
                    Connect
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </>
    );
  }

  renderBody(connections) {
    if (this.isAppStreamEnabled) {
      return this.renderAppStreamBody(connections);
    }

    const processingId = this.processingId;
    const isDisabled = id => processingId !== id && !_.isEmpty(processingId);
    const isLoading = id => processingId === id;

    return (
      <>
        {_.map(connections, item => (
          <div className="connection-row" key={item.id}>
            <div className="connection-row-name">{item.name || 'Web Application'}</div>
            <div className="connection-row-actions">
              <button
                className="connection-btn connection-btn-primary"
                disabled={isDisabled(item.id)}
                onClick={this.handleConnect(item.id)}
                data-testid="connect-to-workspace-button"
              >
                {isLoading(item.id) ? <Icon name="spinner" loading style={{ margin: 0 }} /> : <Icon name="external" style={{ margin: 0 }} />}
                Open in Browser
              </button>
            </div>
          </div>
        ))}
      </>
    );
  }

  renderEmpty() {
    return (
      <div className="connection-empty">
        <Icon name="globe" />
        <div className="connection-empty-title">No HTTP Connections</div>
        <div className="connection-empty-text">This workspace does not have any web connections.</div>
      </div>
    );
  }
}

decorate(ScEnvironmentHttpConnections, {
  envsStore: computed,
  environment: computed,
  connections: computed,
  processingId: observable,
  appStreamGeneratingId: observable,
  appStreamConnectingId: observable,
  destinationUrl: observable,
  streamingUrl: observable,
});

export default inject('scEnvironmentsStore')(withRouter(observer(ScEnvironmentHttpConnections)));
